import { useEffect, useState } from "react";
import { Sheet } from "@components/ui";
import { PhoneIcon } from "@icons";
import { useVoice } from "@lib/voiceContext";
import { useAuth } from "@hooks/useAuth";
import { apiClient } from "@lib/apiClient";
import { fmtNumber, toE164 } from "@lib/phone";
import styles from "./DialerSheet.module.css";

const KEYPAD: { d: string; sub: string }[] = [
  { d: "1", sub: "" }, { d: "2", sub: "ABC" }, { d: "3", sub: "DEF" },
  { d: "4", sub: "GHI" }, { d: "5", sub: "JKL" }, { d: "6", sub: "MNO" },
  { d: "7", sub: "PQRS" }, { d: "8", sub: "TUV" }, { d: "9", sub: "WXYZ" },
  { d: "*", sub: "" }, { d: "0", sub: "+" }, { d: "#", sub: "" },
];

/** App-wide dialpad bottom sheet (Aria iOS v2 design). */
export function DialerSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const voice = useVoice();
  const { user } = useAuth();
  const isSuper = user?.role === "SUPERADMIN";
  const [value, setValue] = useState("");
  const [myNumber, setMyNumber] = useState("");

  useEffect(() => {
    if (!open) return;
    setValue("");
    apiClient
      .get<{ callerId?: string | null }>("/voice/caller-id")
      .then((s) => setMyNumber(s.callerId ?? ""))
      .catch(() => {});
  }, [open]);

  const busy = voice.status === "in-call" || voice.status === "connecting";
  const push = (d: string) => setValue((v) => (v.length < 24 ? v + d : v));
  const dialNow = () => {
    const num = toE164(value, Boolean(isSuper));
    if (num) {
      void voice.dial(num);
      onClose();
    }
  };

  return (
    <Sheet open={open} onClose={onClose}>
      <div className={styles.pad}>
        <div className={`${styles.display} ${value ? styles.displayActive : ""}`}>
          {value ? fmtNumber(value) : "Dial a number"}
        </div>
        <div className={styles.from}>{myNumber ? `Calling from ${fmtNumber(myNumber)}` : " "}</div>
        <div className={styles.keys}>
          {KEYPAD.map((k) => (
            <button
              key={k.d}
              className={styles.key}
              disabled={busy}
              onClick={() => {
                if (k.d === "0") push("0");
                else push(k.d);
              }}
            >
              <span className={styles.digit}>{k.d}</span>
              <span className={styles.sub}>{k.sub || " "}</span>
            </button>
          ))}
        </div>
        <div className={styles.controls}>
          {isSuper ? (
            <button
              className={styles.ctrl}
              disabled={busy}
              onClick={() => setValue((v) => (!v.includes("+") ? "+" + v : v))}
              aria-label="Insert plus for international dialing"
            >
              +
            </button>
          ) : (
            <span className={styles.ctrlSpacer} aria-hidden />
          )}
          <button className={styles.call} onClick={dialNow} disabled={busy || !value} aria-label="Call">
            <PhoneIcon width={26} height={26} />
          </button>
          <button
            className={styles.ctrl}
            disabled={busy || !value}
            onClick={() => setValue((v) => v.slice(0, -1))}
            aria-label="Backspace"
          >
            ⌫
          </button>
        </div>
      </div>
    </Sheet>
  );
}
